import React, { useEffect, useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Loan, RetentionOutcome } from '../types';
import { AlertTriangle, RefreshCw, ArrowLeft, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface RetentionActionModalProps {
  loan: Loan | null;
  isOpen: boolean;
  onClose: () => void;
  onAction: (loanId: string, outcome: RetentionOutcome, notes: string) => void;
}
type Step = 'select' | 'confirm';
export function RetentionActionModal({
  loan,
  isOpen,
  onClose,
  onAction
}: RetentionActionModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [selectedOutcome, setSelectedOutcome] = useState<RetentionOutcome>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setSelectedOutcome(null);
      setNotes('');
      setIsSubmitting(false);
    }
  }, [isOpen]);
  if (!loan) return null;
  const handleOutcomeSelect = (outcome: RetentionOutcome) => {
    setSelectedOutcome(outcome);
    setStep('confirm');
  };
  const handleConfirm = async () => {
    setIsSubmitting(true);
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));
    onAction(loan.id, selectedOutcome, notes);
    setIsSubmitting(false);
    onClose();
  };
  const getOutcomeColor = (outcome: RetentionOutcome) => {
    switch (outcome) {
      case 'Revived - New File':
        return 'bg-accent-50 border-accent-200 text-accent-700';
      case 'Revived - Previous Stage':
        return 'bg-emerald-50 border-emerald-200 text-emerald-700';
      default:
        return 'border-brand-200 bg-brand-50 text-brand-600';
    }
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={step === 'select' ? `Manage Loan #${loan.loanNumber}` : 'Confirm Action'} description={step === 'select' ? 'Select an action to resolve this retention case.' : 'Please review and confirm your decision.'} maxWidth="lg" data-id="element-178" data-mp="395samyga890g">
      <div className="space-y-6" data-id="element-179" data-mp="17i5xo432ns4m">
        {/* Loan Context Header */}
        <div className="grid grid-cols-2 gap-4 rounded-lg border border-brand-100 bg-brand-50 p-4 text-sm" data-id="element-180" data-mp="sqxas2qk0kv6">
          <div data-id="element-181" data-mp="xop1itfgq0xs">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-brand-400" data-id="element-182" data-mp="xo707idy0wj0">
              Borrower
            </span>
            <span className="font-bold text-brand-500" data-id="element-183" data-mp="ntf12a4pq1m4">
              {loan.borrowerName}
            </span>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-brand-400" data-id="element-184" data-mp="9tw5shx02kr8">
              <Phone className="h-3 w-3" data-id="element-185" data-mp="eqw4g7risl8b" /> {loan.borrowerPhone}
            </div>
          </div>
          <div data-id="element-186" data-mp="246cahea5dqtz">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-brand-400" data-id="element-187" data-mp="247x32ycwizp7">
              Loan Officer
            </span>
            <span className="font-bold text-brand-500" data-id="element-188" data-mp="3tnr19t2x56jw">{loan.loanOfficer}</span>
          </div>
          <div className="col-span-2" data-id="element-189" data-mp="3ooo6d1gnkgpn">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-brand-400" data-id="element-190" data-mp="3oppil99en98y">
              Retention Reason
            </span>
            <span className="inline-block rounded border border-accent-200 bg-accent-50 px-2 py-1 font-semibold text-accent-800" data-id="element-191" data-mp="79zllg2u857x">
              {loan.retentionComment}
            </span>
          </div>
        </div>

        <AnimatePresence mode="wait" data-id="element-192" data-mp="ba071koctgoh">
          {step === 'select' ? <motion.div key="select" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-193" data-mp="bbum4icy2ekn">
              <button onClick={() => handleOutcomeSelect('Revived - New File')} className="group flex h-48 flex-col items-center justify-center rounded-xl border-2 border-brand-100 p-6 text-center transition-colors hover:border-accent-200 hover:bg-accent-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500" data-id="element-194" data-mp="2n1nen0eqddlg">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-600 transition-transform group-hover:scale-110" data-id="element-195" data-mp="1s010ygm9hepd">
                  <RefreshCw className="h-6 w-6" data-id="element-196" data-mp="10bqxll6njzmy" />
                </div>
                <h3 className="mb-2 font-bold text-brand-500" data-id="element-197" data-mp="14c10goz5e9xl">
                  Revive - New File
                </h3>
                <p className="text-xs text-brand-400" data-id="element-198" data-mp="194avukj18279">
                  Restart process with a new loan number.
                </p>
              </button>

              <button onClick={() => handleOutcomeSelect('Revived - Previous Stage')} className="group flex h-48 flex-col items-center justify-center rounded-xl border-2 border-brand-100 p-6 text-center transition-colors hover:border-emerald-200 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500" data-id="element-199" data-mp="gmyjzkg92n4o">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 transition-transform group-hover:scale-110" data-id="element-200" data-mp="3r92cpjh6a7v8">
                  <ArrowLeft className="h-6 w-6" data-id="element-201" data-mp="3mba8pr396vao" />
                </div>
                <h3 className="mb-2 font-bold text-brand-500" data-id="element-202" data-mp="3hi7oi9kd8y75">
                  Revive - Previous
                </h3>
                <p className="text-xs text-brand-400" data-id="element-203" data-mp="3ckpbo8389ytv">
                  Return file to the previous processing stage.
                </p>
              </button>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} className="space-y-6" data-id="element-204" data-mp="33iy6d4clnu0j">
              <div className={`p-4 rounded-lg border flex items-center gap-3 ${getOutcomeColor(selectedOutcome)}`} data-id="element-205" data-mp="2ezo5qs9g8pvl">
                <AlertTriangle className="h-5 w-5 shrink-0" data-id="element-206" data-mp="2jziuoq9nz5e0" />
                <div data-id="element-207" data-mp="37oleo4rrtabo">
                  <p className="text-sm font-medium" data-id="element-208" data-mp="37pd8ba1t4xzb">
                    You are about to:{' '}
                    <span className="font-bold" data-id="element-209" data-mp="3cog21ycc7hrz">{selectedOutcome}</span>
                  </p>
                  <p className="text-xs opacity-90" data-id="element-210" data-mp="2xx785d1eja91">
                    This action will be recorded in the loan history.
                  </p>
                </div>
              </div>

              <div data-id="element-211" data-mp="3qkl87k0eclxd">
                <label className="mb-2 block text-sm font-semibold text-brand-600" data-id="element-212" data-mp="3qk1pw26u3vit">
                  Resolution Notes{' '}
                  <span className="font-normal text-brand-400" data-id="element-213" data-mp="1jxm781kkwrxd">(Optional)</span>
                </label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any context about this decision..." className="w-full min-h-[100px] resize-none rounded-md border border-brand-200 p-3 text-sm text-brand-500 placeholder:text-brand-300 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500" data-id="element-214" data-mp="1ez2hdq2v6vzq" />
              </div>

              <div className="flex items-center justify-between border-t border-brand-100 pt-4" data-id="element-215" data-mp="w1rja8syy8lv">
                <Button variant="ghost" onClick={() => setStep('select')} disabled={isSubmitting} data-id="element-216" data-mp="1a110nbswv8rf">
                  Back
                </Button>
                <Button variant="primary" onClick={handleConfirm} isLoading={isSubmitting} data-id="element-217" data-mp="2lgpnm36607s8">
                  Confirm & Resolve
                </Button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </Modal>;
}